import { axios_tenant, RedirectTenantLogin } from "../../../../axios";
import { downloadBlobResponse, getErrorMessageFromBlob, XLSX_CONTENT_TYPE } from "../utils/downloadHelpers";
import { BASE_URL, TEMPLATE_FILENAME } from "./constants";

/**
 * Calls for the inventor bulk upload endpoints.
 *
 * Every request is marked `silent` so this screen can render errors inline next to the
 * dropzone instead of firing the global toast the axios interceptor adds by default -
 * a failed upload needs the wrong-template message on screen, not a toast that vanishes.
 */
const silent = { silent: true };

/**
 * `silent` stops the interceptor at its first line, which skips the session handling it
 * would otherwise do as well as the toast. Reinstating just the redirect keeps that
 * trade local to this screen: an expired session belongs at the login page, not rendered
 * as "Request failed with status code 401" in an Alert the user can do nothing about.
 */
const withSessionRedirect = async (request) => {
  try {
    return await request();
  } catch (error) {
    if (error?.response?.status === 401) RedirectTenantLogin();
    throw error;
  }
};

export const downloadTemplate = async () => {
  const response = await withSessionRedirect(() =>
    axios_tenant.get(`${BASE_URL}/template`, {
      responseType: "blob",
      ...silent,
    }));
  await downloadBlobResponse(response, TEMPLATE_FILENAME, XLSX_CONTENT_TYPE);
};

/**
 * The upload always runs against one client profile — the one whose Inventor Information
 * section it was started from. Every imported inventor is attached to it; the file itself
 * carries no client column.
 */
export const uploadInventorFile = async (file, clientProfileId) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("clientProfileId", clientProfileId);

  const response = await withSessionRedirect(() =>
    axios_tenant.post(`${BASE_URL}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...silent,
    }));
  return response.data;
};

export const fetchBatches = async (clientProfileId) => {
  const response = await withSessionRedirect(() =>
    axios_tenant.get(`${BASE_URL}/batches`, {
      params: { clientProfileId },
      ...silent,
    }));
  return response.data;
};

export const downloadValidationResult = async (batchId) => {
  const response = await withSessionRedirect(() =>
    axios_tenant.get(`${BASE_URL}/batches/${batchId}/result`, {
      responseType: "blob",
      ...silent,
    }));
  await downloadBlobResponse(response, `Validation_Result_${batchId}.xlsx`, XLSX_CONTENT_TYPE);
};

/**
 * The message to show the user for a failed request. A blob-typed body means the request
 * asked for a file but got a JSON error back, so the message has to be read out of it.
 */
export const errorMessageFrom = async (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof Blob) return getErrorMessageFromBlob(data, fallback);
  return data?.message || error?.message || fallback;
};
